import React, {  useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Fuel, 
  ArrowRight, 
  CheckCircle, 
  TrendingUp, 
  Users, 
  BarChart3,
  Clock,
  Shield,
  Target,
  Eye,
  Play,
  AlertTriangle,
  Gauge,
  Droplets,
} from 'lucide-react';
import { ImageWithFallback } from '../../figma/ImageWithFallback';

const OilGasPage: React.FC = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef });
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  
  ;

  const challenges = [
    'Equipment failures in remote and harsh operating environments',
    'Ensuring worker safety in high-risk oil and gas extraction sites',
    'Meeting stringent environmental and regulatory compliance requirements',
    'Managing large-scale HR operations across multiple remote locations',
    'Lack of real-time monitoring for equipment performance and worker safety',
    'Coordinating complex projects across distributed teams and challenging terrains'
  ];

  const solutions = [
    {
      name: 'MVS x AI',
      path: '/products/ai/mvsxAi',
      description: 'Get faster, denser models with robust depth, occlusion handling, and photoreal textures.',
      benefits: ['Real-time meshing', 'Depth fusion', 'Neural reconstruction']
    },
    {
      name: 'Predictive Maintenance',
      path: '/products/ai/predictivemaintenance',
      description: 'Anticipate equipment failures before they happen using sensors, analytics, and AI.',
      benefits: ['Optimized scheduling', 'Failure forecasting', 'Anomaly detection']
    },
    {
      name: 'Asset Management',
      path: '/products/ai/assetmanagement',
      description: 'Track, control, and grow your portfolio of physical and digital assets.',
      benefits: ['Portfolio analytics', 'Risk optimization', 'Lifecycle tracking']
    },

  ];

  const keyFeatures = [
    {
      icon: Shield,
      title: 'Safety-First Operations',
      description: 'Advanced monitoring systems that prioritize worker safety in high-risk oil and gas environments.'
    },
    {
      icon: Gauge,
      title: 'Real-Time Equipment Intelligence',
      description: 'Continuous monitoring and predictive analytics for critical oil and gas extraction equipment.'
    },
    {
      icon: Target,
      title: 'Environmental Compliance',
      description: 'Comprehensive environmental monitoring and regulatory compliance management systems.'
    },
    {
      icon: Users,
      title: 'Distributed Workforce Management',
      description: 'Efficient management of remote teams across multiple oil and gas extraction sites.'
    }
  ];

  const benefits = [
    {
      icon: Clock,
      title: 'Equipment Uptime',
      description: 'Predictive maintenance and real-time monitoring significantly reduce equipment downtime and operational disruptions.',
      metric: '90% uptime improvement'
    },
    {
      icon: Shield,
      title: 'Safety Performance',
      description: 'Advanced safety monitoring and compliance systems drastically reduce workplace incidents and accidents.',
      metric: '85% safety improvement'
    },
    {
      icon: BarChart3,
      title: 'Operational Efficiency',
      description: 'Intelligent automation and real-time analytics optimize operational efficiency across all sites.',
      metric: '70% efficiency gain'
    },
    {
      icon: TrendingUp,
      title: 'Environmental Compliance',
      description: 'Automated environmental monitoring ensures consistent adherence to regulatory standards.',
      metric: '95% compliance rate'
    }
  ];

  return (
    <div ref={containerRef} className="relative min-h-screen pt-20">
      {/* Animated Background */}
      <motion.div
        style={{ y: backgroundY }}
        className="fixed inset-0 opacity-20"
      >
        <div className="absolute inset-0">
          {Array.from({ length: 6 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-6 h-6 bg-yellow-500 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                scale: [0, 1, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 7,
                delay: i * 0.5,
                repeat: Infinity,
                repeatType: "loop"
              }}
            />
          ))}
        </div>
      </motion.div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="flex items-center gap-3 mb-6"
              >
                <div className="p-3 rounded-xl bg-linear-to-br from-yellow-500 to-amber-600 neon-glow">
                  <Fuel className="w-8 h-8 text-white" />
                </div>
                <div>
                  <span className="text-sm font-medium text-yellow-400 uppercase tracking-wide">Smart Energy Technology</span>
                  <h1 className="text-4xl lg:text-6xl font-bold">
                    Oil & Gas <span className="bg-linear-to-r from-yellow-500 to-amber-600 bg-clip-text text-transparent">Industry</span>
                  </h1>
                </div>
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="text-2xl font-semibold mb-4 text-yellow-300"
              >
                Powering the Future of Energy Operations
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-xl text-muted-foreground mb-8 leading-relaxed"
              >
                The oil and gas industry operates in some of the world's most challenging environments, where safety, 
                efficiency, and environmental responsibility are paramount. From offshore drilling platforms to remote 
                extraction sites, companies need intelligent systems that can predict equipment failures, ensure worker 
                safety, and maintain environmental compliance. At amasQIS.ai, we deliver robust AI, IoT, and automation 
                solutions designed specifically for the demanding requirements of energy operations.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 mb-12"
              >
                <Link
                  to="/contact"
                  className="group inline-flex items-center justify-center gap-3 bg-linear-to-r from-yellow-500 to-amber-600 px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-yellow-500/25 transition-all duration-300"
                >
                  Power Operations
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <button className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300">
                  <Play className="w-5 h-5" />
                  Watch Demo
                </button>
              </motion.div>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, x: 100, rotateY: -15 }}
              animate={{ opacity: 1, x: 0, rotateY: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="relative perspective-1000"
            >
              <div className="relative group">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 45, repeat: Infinity, ease: "linear" }}
                  className="absolute -inset-10 bg-linear-to-r from-yellow-500 to-amber-600 opacity-20 rounded-full blur-3xl"
                />
                
                <motion.div
                  whileHover={{ scale: 1.05, rotateY: 5, rotateX: 5 }}
                  transition={{ duration: 0.4 }}
                  className="relative z-10 glass-strong rounded-2xl overflow-hidden transform-gpu"
                >
                  <ImageWithFallback
                    src='/assets/Industries/oil-gas.png'
                    alt="Oil & Gas Industry"
                    className="w-full h-96 object-cover"
                  />
                  
                  <div className="absolute inset-0 bg-linear-to-t from-black/80 via-transparent to-transparent">
                    <div className="absolute bottom-6 left-6 right-6">
                      <div className="flex items-center gap-3 text-white">
                        <div className="p-2 bg-yellow-500/20 rounded-lg backdrop-blur-sm">
                          <Fuel className="w-5 h-5 text-yellow-500" />
                        </div>
                        <div>
                          <p className="font-semibold">AI-Powered</p>
                          <p className="text-sm text-white/80">Energy Solutions</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 1 }}
                  className="absolute -top-4 -right-4 glass p-3 rounded-xl"
                >
                  <div className="flex items-center gap-2 text-yellow-500">
                    <Droplets className="w-4 h-4 animate-pulse" />
                    <span className="text-xs font-mono">ENERGY</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Challenges Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Challenges in the Oil & Gas Sector
            </h2>
            <p className="text-lg text-muted-foreground text-center max-w-3xl mx-auto">
              Energy operations face unique challenges that require specialized solutions for safety, efficiency, and environmental stewardship.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {challenges.map((challenge, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="glass p-6 rounded-xl hover:glass-strong transition-all duration-300"
              >
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5 shrink-0" />
                  <p className="text-muted-foreground">{challenge}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Our Solutions for Oil & Gas
            </h2>
            <p className="text-lg text-muted-foreground text-center max-w-3xl mx-auto">
              Comprehensive energy solutions designed to enhance safety, optimize operations, and ensure environmental compliance in demanding conditions.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {solutions.map((solution, index) => (
              <Link
                key={solution.name}
                to={solution.path}
                className="block group h-full"
              >
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                  className="glass p-8 rounded-xl hover:glass-strong transition-all duration-300 cursor-pointer h-full flex flex-col"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-xl font-semibold text-yellow-400 group-hover:text-yellow-300 transition-colors">
                      {solution.name}
                    </h3>
                    <ArrowRight className="w-5 h-5 text-yellow-400 group-hover:translate-x-1 transition-transform" />
                  </div>
                  <p className="text-muted-foreground mb-4">{solution.description}</p>
                  <div className="space-y-2">
                    {solution.benefits.map((benefit) => (
                      <div key={benefit} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-amber-400" />
                        <span className="text-sm">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Why Oil & Gas Companies Choose amasQIS.ai
            </h2>
            <p className="text-lg text-muted-foreground text-center max-w-3xl mx-auto">
              Our solutions are engineered for the extreme conditions and critical requirements of energy operations.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {keyFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                  className="glass p-6 rounded-xl hover:glass-strong transition-all duration-300 text-center"
                >
                  <div className="mb-4 flex justify-center">
                    <Icon className="w-8 h-8 text-yellow-500" />
                  </div>
                  <h3 className="text-lg font-semibold mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Measurable Energy Impact
            </h2>
            <p className="text-lg text-muted-foreground text-center max-w-3xl mx-auto">
              Transform your energy operations with quantifiable improvements in safety, efficiency, and environmental compliance.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className="glass p-8 rounded-xl hover:glass-strong transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-yellow-500/20">
                      <Icon className="w-6 h-6 text-yellow-500" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold mb-2">{benefit.title}</h3>
                      <p className="text-muted-foreground mb-3">{benefit.description}</p>
                      <div className="text-amber-400 font-semibold">{benefit.metric}</div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-15">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Ready to Power the Future?
            </h2>
            <p className="text-lg text-muted-foreground text-center mb-8">
              Join leading energy companies that trust amasQIS.ai to deliver safer, smarter, and more sustainable oil and gas operations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center gap-3 bg-linear-to-r from-yellow-500 to-amber-600 px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-yellow-500/25 transition-all duration-300"
              >
                Power Operations
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/solutions"
                className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300"
              >
                Explore Solutions
                <Eye className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export { OilGasPage };
